import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { boringBucketService } from '../../services/boringBucketService';
import '../Dashboard.css';

const SSO_KEY = 'discourse_sso_params';

/**
 * DiscourseConnect SSO bridge. Discourse redirects the browser here with
 * ?sso=&sig=. We authenticate the WFS user; the backend verifies Boring Bucket
 * membership, signs the response, and we hand the browser back to Discourse
 * already logged in. The sso/sig pair is held in sessionStorage so it survives
 * a login round-trip.
 */
export const DiscourseSso: React.FC = () => {
  const { user, token, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'working' | 'not-member' | 'error'>('working');
  const startedRef = useRef(false);

  useEffect(() => {
    // Stash sso/sig as soon as they arrive so they survive a login round-trip.
    let sso = searchParams.get('sso');
    let sig = searchParams.get('sig');
    if (sso && sig) {
      sessionStorage.setItem(SSO_KEY, JSON.stringify({ sso, sig }));
    } else {
      const saved = sessionStorage.getItem(SSO_KEY);
      if (saved) {
        try { ({ sso, sig } = JSON.parse(saved)); } catch { /* ignore */ }
      }
    }

    if (!sso || !sig) { setStatus('error'); return; }

    // Wait for the auth context to finish hydrating — otherwise `user` is
    // briefly null on a fresh page load and we'd wrongly bounce to login.
    if (loading) return;

    // Genuinely not signed in — go log in, then return here (sso/sig held in
    // sessionStorage; Login redirects back to /discourse-sso).
    if (!user || !token) {
      navigate('/login?redirect=discourse-sso');
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    boringBucketService.discourseSso(token, sso, sig)
      .then(({ redirectUrl }) => {
        sessionStorage.removeItem(SSO_KEY);
        window.location.href = redirectUrl;
      })
      .catch((err) => {
        startedRef.current = false;
        setStatus(err?.response?.status === 403 ? 'not-member' : 'error');
      });
  }, [user, token, loading, searchParams, navigate]);

  return (
    <div className="dashboard-container">
      {user && <Navigation />}
      <div className="dashboard-content">
        <div style={{ padding: '2rem', maxWidth: 520 }}>
          {status === 'working' && (
            <>
              <h2>Signing you in to the community…</h2>
              <p style={{ color: '#94a3b8' }}>
                One moment — taking you to The Boring Bucket community.
              </p>
            </>
          )}
          {status === 'not-member' && (
            <>
              <h2>Boring Bucket membership required</h2>
              <p style={{ color: '#94a3b8' }}>
                The community is for Boring Bucket members only.{' '}
                <Link to="/boring-bucket" style={{ color: '#60a5fa' }}>
                  Learn about membership →
                </Link>
              </p>
            </>
          )}
          {status === 'error' && (
            <>
              <h2>Could not sign you in</h2>
              <p style={{ color: '#94a3b8' }}>
                Something went wrong reaching the community. Open it directly at{' '}
                <a href="https://community.theboringbucket.com" style={{ color: '#60a5fa' }}>
                  community.theboringbucket.com
                </a>
                , or try again from The Boring Bucket section.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscourseSso;
